import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { QueryKeys } from '../constants/query_keys';
import { apiV1 } from '../lib/api';
import { FIVE_MINUTES_IN_MS } from '../lib/time';
import { FlashMessageActions } from '../redux/flash_message';
import { useUser } from './use_user';

export const useWaypointFilters = () => {
  const dispatch = useDispatch();
  const { user } = useUser();
  const queryClient = useQueryClient();

  const waypointFiltersQuery = useQuery({
    queryKey: QueryKeys.ME.WAYPOINT_FILTERS,
    queryFn: async () => {
      const res = await apiV1.userWaypointFiltersFetch();
      return res.data.data;
    },
    staleTime: FIVE_MINUTES_IN_MS,
    enabled: user != null && user.premium_pro,
  });
  const { data: waypointFilters = [] } = waypointFiltersQuery;

  const createWaypointFilterMutation = useMutation({
    mutationFn: async (params: Parameters<typeof apiV1.userWaypointFilterCreate>[0]) => {
      const res = await apiV1.userWaypointFilterCreate(params);
      return res.data.data;
    },
    onSuccess: () => {
      // TODO: improvement: add optimistic update alongside invalidating queries
      queryClient.invalidateQueries({ queryKey: QueryKeys.ME.WAYPOINT_FILTERS });
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: `Waypoint Filter Saved!`,
          subheader: `Your Waypoint Filter has been saved.`,
        })
      );
    },
    onError: (error) => {
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: `We're sorry, there was a problem saving this Waypoint Filter.`,
          subheader: 'Please try again or contact support.',
          type: 'error',
        })
      );
    },
  });

  const updateWaypointFilterMutation = useMutation({
    mutationFn: async (params: {
      id: number;
      updateParams: Parameters<typeof apiV1.userWaypointFilterUpdate>[1];
    }) => {
      const { id, updateParams } = params;
      const res = await apiV1.userWaypointFilterUpdate(id, updateParams);
      return res.data.data;
    },
    onSuccess: (waypointFilter) => {
      // TODO: improvement: add optimistic update alongside invalidating queries
      queryClient.invalidateQueries({ queryKey: QueryKeys.ME.WAYPOINT_FILTERS });
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: `${waypointFilter.name} Updated!`,
          subheader: `Your Waypoint Filter has been updated`,
        })
      );
    },
    onError: (error) => {
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: `We're sorry, there was a problem saving this Waypoint Filter`,
          subheader: 'Please try again or contact support',
          type: 'error',
        })
      );
    },
  });

  const deleteWaypointFilterMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiV1.userWaypointFilterDelete(id);
    },
    onSuccess: () => {
      // TODO: improvement: add optimistic update alongside invalidating queries
      queryClient.invalidateQueries({ queryKey: QueryKeys.ME.WAYPOINT_FILTERS });
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: 'Waypoint Deleted',
          subheader: 'Your Waypoint has been deleted',
        })
      );
    },
    onError: (error) => {
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: 'Your Waypoint Filter Failed to Delete',
          subheader: ' Please try again. If this issue persists, please contact support.',
          type: 'error',
        })
      );
    },
  });

  return {
    createWaypointFilter: createWaypointFilterMutation,
    deleteWaypointFilter: deleteWaypointFilterMutation,
    updateWaypointFilter: updateWaypointFilterMutation,
    waypointFilters,
  };
};
